import React, { useState } from 'react';
import { 
  Send, 
  Loader2, 
  FileText, 
  BookOpen,
  Sparkles,
  ExternalLink
} from 'lucide-react';

const AgentPage = () => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

  // Fetch debug info on component mount
  React.useEffect(() => {
    const fetchDebugInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v6/agent/debug`);
        const data = await response.json();
        if (data.success) {
          setDebugInfo(data);
        }
      } catch (err) {
        console.error('Error fetching debug info:', err);
      }
    };
    fetchDebugInfo();
  }, [API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const response = await fetch(`${API_URL}/api/v6/agent/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question.trim(),
          similarity_threshold: 0.5,
          max_results: 10
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get response from agent');
      }

      const data = await response.json();
      setResponse(data);
      console.log('Agent response:', data);
      if (data.debug) {
        console.log('Debug info:', data.debug);
      }
    } catch (err) {
      console.error('Error querying agent:', err);
      setError(err.message || 'An error occurred while processing your question');
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (sourceType) => {
    return sourceType === 'pdf' ? <FileText className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />;
  };

  const getSourceUrl = (source) => {
    if (source.source_type === 'word' && source.id) {
      // Word documents - could link to article detail page if we have one
      return null;
    } else if (source.source_type === 'pdf' && source.document_id) {
      // PDF documents - link to v5 document detail
      return `/v5?document_id=${source.document_id}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <Sparkles className="h-8 w-8 text-purple-600" />
              <h1 className="text-3xl font-bold text-gray-900">AI Agent</h1>
            </div>
            {debugInfo && (
              <button
                onClick={() => setShowDebug(!showDebug)}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
              >
                {showDebug ? 'Hide' : 'Show'} Debug Info
              </button>
            )}
          </div>
          <p className="text-gray-600">
            Ask questions about your articles. The agent will search through your content library using semantic search and provide answers based on the retrieved information.
          </p>
          
          {/* Debug Info */}
          {showDebug && debugInfo && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Embedding Status</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-blue-800">Word Documents:</p>
                  <ul className="text-blue-700 ml-4">
                    <li>Total: {debugInfo.word_documents.total}</li>
                    <li>With Text: {debugInfo.word_documents.with_text}</li>
                    <li>With Embeddings: {debugInfo.word_documents.with_embeddings}</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-blue-800">PDF Extractions:</p>
                  <ul className="text-blue-700 ml-4">
                    <li>Total: {debugInfo.pdf_extractions.total}</li>
                    <li>With Text: {debugInfo.pdf_extractions.with_text}</li>
                    <li>With Embeddings: {debugInfo.pdf_extractions.with_embeddings}</li>
                  </ul>
                </div>
              </div>
              <p className="mt-2 text-sm text-blue-800">
                <strong>Total Embeddings Available:</strong> {debugInfo.total_embeddings}
              </p>
              {debugInfo.total_embeddings === 0 && (
                <p className="mt-2 text-sm text-red-700 font-medium">
                  ⚠️ No embeddings found! You need to generate embeddings first. Check the backend logs or run the embedding generation script.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Question Input */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <form onSubmit={handleSubmit}>
            <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
              Ask me anything
            </label>
            <div className="flex space-x-3">
              <textarea
                id="question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="e.g., What are the best practices for financial planning?"
                className="flex-1 min-h-[100px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !question.trim()}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>Ask</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 font-medium mb-2">Error:</p>
            <p className="text-red-700">{error}</p>
            <p className="text-sm text-red-600 mt-2">
              Check the browser console (F12) and backend logs for more details.
            </p>
          </div>
        )}
        
        {/* Debug info in response */}
        {response && response.debug && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-yellow-900 mb-2">Debug Information</h3>
            <pre className="text-xs text-yellow-800 overflow-auto">
              {JSON.stringify(response.debug, null, 2)}
            </pre>
          </div>
        )}

        {/* Response */}
        {response && (
          <div className="space-y-6">
            {/* AI Answer */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-900">Answer</h2>
              </div>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{response.answer}</p>
              </div>
            </div>

            {/* Sources */}
            {response.sources && response.sources.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Related Articles ({response.count})
                    </h2>
                  </div>
                </div>
                <div className="space-y-4">
                  {response.sources.map((source, index) => {
                    const sourceUrl = getSourceUrl(source);
                    return (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              {getSourceIcon(source.source_type)}
                              <h3 className="font-semibold text-gray-900">{source.title}</h3>
                              <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                                {source.source_type.toUpperCase()}
                              </span>
                              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                {source.similarity}% match
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              <span>By {source.author}</span>
                              {source.folder_name && (
                                <span className="ml-2">• {source.folder_name}</span>
                              )}
                            </div>
                            {source.text_preview && (
                              <p className="text-sm text-gray-700 line-clamp-2">
                                {source.text_preview}
                              </p>
                            )}
                          </div>
                          {sourceUrl && (
                            <a
                              href={sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-4 text-purple-600 hover:text-purple-700"
                            >
                              <ExternalLink className="h-5 w-5" />
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!response && !loading && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Sparkles className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ready to answer your questions
            </h3>
            <p className="text-gray-600">
              Enter a question above to search through your article library and get an AI-powered answer.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentPage;

